import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowDown, Clock, RotateCw } from 'lucide-react';
import { cn } from 'lib/utils';
import { BATCH_PARENT_ENTITIES, dayKey, numericDate } from 'utils/auditLabels';
import type { AdminAuditEvent } from 'api/adminAuditApi';
import { GRID_COLS, type OpenEvent } from './tableColumns';
import EventRow from './EventRow';
import ImportRow from './ImportRow';
import SkeletonRows from './SkeletonRows';
import FullWidthRow from './FullWidthRow';
import RetentionNotice from 'components/admin/audit/RetentionNotice';

interface AuditTableProps {
  events: AdminAuditEvent[];
  total: number;
  since: string | null;
  retentionMonths: number | null;
  isLoading: boolean;
  isError: boolean;
  /** True when actor/module/action/search/importId narrows the query, or the period is anything
   * but "Tout l'historique" — the "depuis le {since}" empty message is only honest without this
   * (fix round 1, CA12). Distinct from the filter bar's own Réinitialiser trigger. */
  narrowsResults: boolean;
  onRetry: () => void;
  onResetFilters: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onLoadMore: () => void;
  onOpenEvent: OpenEvent;
}

/**
 * The journal's table (J13, design §6.4) — props only, the page owns the query and the filters.
 * A batch parent row (import, realignment) expands its own children on demand (`ImportRow`); every other row (see
 * `EventRow`) opens the shared `EventDrawer` on click, Enter or Space.
 */
const AuditTable = ({
  events,
  total,
  since,
  retentionMonths,
  isLoading,
  isError,
  narrowsResults,
  onRetry,
  onResetFilters,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onLoadMore,
  onOpenEvent,
}: AuditTableProps) => {
  const { t } = useTranslation();

  const header = (
    <div role="row" className={cn('grid items-center gap-3 border-b bg-muted/40 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[.05em] text-muted-foreground', GRID_COLS)}>
      <span role="columnheader">{t('Date')}</span>
      <span role="columnheader">{t('Actor')}</span>
      <span role="columnheader">{t('Module')}</span>
      <span role="columnheader">{t('Entity')}</span>
      <span role="columnheader">{t('Action')}</span>
      <span role="columnheader">{t('Summary')}</span>
    </div>
  );

  if (isLoading) {
    return (
      <div role="table">
        {header}
        <SkeletonRows />
      </div>
    );
  }

  if (isError && events.length === 0) {
    return (
      <div role="table">
        {header}
        <FullWidthRow>
          <div role="alert" className="flex flex-col items-center gap-2 px-4 py-14 text-center">
            <AlertCircle className="h-9 w-9 text-rose-500" aria-hidden="true" />
            <p className="text-[13.5px] font-medium">{t('Unable to load the journal')}</p>
            <p className="max-w-[320px] text-[12.5px] text-muted-foreground">{t('The filters are kept. Try again in a moment.')}</p>
            <button
              type="button"
              onClick={onRetry}
              className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-[12.5px] font-medium hover:bg-accent"
            >
              <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
              {t('Retry')}
            </button>
          </div>
        </FullWidthRow>
      </div>
    );
  }

  if (events.length === 0) {
    // `since` is the oldest row of the WHOLE table, so it settles the branch first: null means
    // the table has genuinely never had a row, true regardless of any filter. Otherwise, a period
    // restriction (including the plain 30-day default) narrows the result set just like any other
    // filter — "depuis le {since}" is only honest once nothing at all restricts the query
    // (fix round 1, CA12).
    const filteredEmpty = since !== null && narrowsResults;
    return (
      <div role="table">
        {header}
        <FullWidthRow>
          <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
            <span className="flex h-10 w-10 items-center justify-center rounded-[11px] bg-accent" aria-hidden="true">
              <Clock className="h-5 w-5 text-muted-foreground" />
            </span>
            {filteredEmpty ? (
              <>
                <p className="text-[13.5px] font-medium">{t('No actions match these filters')}</p>
                <p className="max-w-[320px] text-[12.5px] text-muted-foreground">{t('Widen the period or remove a filter.')}</p>
                <button type="button" onClick={onResetFilters} className="mt-1 text-[12.5px] font-medium text-muted-foreground underline hover:text-foreground">
                  {t('Reset')}
                </button>
              </>
            ) : (
              <p className="text-[13.5px] font-medium">
                {since ? t('No actions recorded since {{date}}', { date: numericDate(dayKey(since)) }) : t('No actions recorded yet')}
              </p>
            )}
          </div>
        </FullWidthRow>
      </div>
    );
  }

  return (
    <>
      <div role="table">
        {header}
        <div role="rowgroup">
          {events.map((event) =>
            BATCH_PARENT_ENTITIES.includes(event.entityType) ? (
              <ImportRow key={event.id} event={event} onOpen={onOpenEvent} />
            ) : (
              <EventRow key={event.id} event={event} onOpen={onOpenEvent} />
            )
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          {isFetchNextPageError ? (
            <span role="alert" className="inline-flex flex-wrap items-center gap-2 text-[12.5px] text-rose-600 dark:text-rose-400">
              {t('An error occurred')}
              <button type="button" onClick={onLoadMore} className="inline-flex items-center gap-1.5 font-medium underline hover:no-underline">
                <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                {t('Retry')}
              </button>
            </span>
          ) : (
            hasNextPage && (
              <button
                type="button"
                disabled={isFetchingNextPage}
                onClick={onLoadMore}
                className="inline-flex items-center gap-1.5 rounded-md border border-input px-3 py-1.5 text-[12.5px] font-medium hover:bg-accent disabled:opacity-50"
              >
                <ArrowDown className={cn('h-3.5 w-3.5', isFetchingNextPage && 'animate-spin')} aria-hidden="true" />
                {t('Load more')}
              </button>
            )
          )}
          <span className="text-[12px] text-muted-foreground">{t('{{shown}} shown out of {{total}}', { shown: events.length, total })}</span>
        </div>
        <span className="text-[12px] text-muted-foreground">
          <RetentionNotice months={retentionMonths} />
        </span>
      </div>
    </>
  );
};

export default AuditTable;
